import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { LiveCourier, LiveMap, LiveStop } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatTime } from "../../lib/time";
import {
  courierColour,
  fitView,
  panned,
  tileLevelOf,
  tileUrl,
  toScreen,
  visibleTiles,
  zoomedAbout,
  type Point,
  type Size,
  type View,
} from "./view";
import { useGlide } from "./glide";

interface Props {
  /** Goes up when an order or a courier changed on any device: the map reloads at once. */
  revision: number;
}

/** A courier's phone tells where he is every 30 metres or 45 seconds: ten seconds keeps the map current. */
const REFRESH_MS = 10_000;
const MIN_LEVEL = 5;
const MAX_LEVEL = 11;
/** One step of zoom (a button, a notch of the wheel, a double tap) takes this long. */
const ZOOM_MS = 260;
/** After a pinch or a trackpad the map settles on the nearest level, so its tiles are sharp. */
const SETTLE_AFTER_MS = 160;
/** A wheel moving this much at once is a notch of a mouse: one level, not a slide. */
const NOTCH = 50;

const easeOut = (time: number) => 1 - (1 - time) ** 3;
/** Who asked the system for less motion gets each zoom at once. */
const lessMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const distance = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y);

/** Back first, first; who did not leave yet after those on the road. */
const byReturn = (a: LiveCourier, b: LiveCourier) =>
  (a.backAt === null ? Number.POSITIVE_INFINITY : Date.parse(a.backAt)) -
    (b.backAt === null ? Number.POSITIVE_INFINITY : Date.parse(b.backAt)) || a.name.localeCompare(b.name, "nl");

const placed = (stop: LiveStop): Point | null => (stop.x === null || stop.y === null ? null : { x: stop.x, y: stop.y });

/**
 * Live-kaart (Furkan, 09/10/2026: "zet daar alle koeriers hun locaties zodat restaurant kan inschatten als
 * de koerier dichter bij is dat ze aan de volgende bestelling starten"): the grey map of Flanders with the
 * shop, every courier on a ride where his phone last told, the addresses he still has to do and when he is
 * back; on the right the same per courier, and the deliveries nobody took yet.
 *
 * The map follows the couriers by itself until someone drags or zooms it; "Alles tonen" lets it follow again.
 * "Bestellingen tonen" puts the deliveries nobody took yet on the map, with their street (Furkan, 25/10/2026:
 * "afgeleverd olmayanlar ve koerier tarafından alınmayanlar görünsün"), so the shop sees which way each one goes.
 */
export function LiveMapScreen({ revision }: Props) {
  const { api, report } = useTill();
  const text = t.liveMap;
  const [map, setMap] = useState<LiveMap | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(
    (signal?: AbortSignal) =>
      api
        .request<LiveMap>("GET", "/device/couriers/live", { retry: true, ...(signal ? { signal } : {}) })
        .then((answer) => {
          setMap(answer);
          setNow(Date.now());
        })
        .catch((error: unknown) => {
          if (!signal?.aborted) report(error);
        }),
    [api, report],
  );
  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    const timer = window.setInterval(() => void load(controller.signal), REFRESH_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [load, revision]);

  if (!map) return <div className="panel livemap" aria-busy="true" />;
  return <LiveMapView map={map} now={now} text={text} />;
}

/** The map and the list, for a Live-kaart as the server gave it (separate so that it can be tested). */
export function LiveMapView({ map, now, text = t.liveMap }: { map: LiveMap; now: number; text?: typeof t.liveMap }) {
  const frame = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<Size>({ width: 800, height: 560 });
  /** Set when someone dragged or zoomed: the map stops following until "Alles tonen". */
  const [chosen, setChosen] = useState<View | null>(null);
  /** The deliveries nobody took yet on the map: only after "Bestellingen tonen". */
  const [ordersShown, setOrdersShown] = useState(false);
  /**
   * The level of the tiles before a zoom: drawn under the new ones, to the scale of the view, until those
   * are all there, so the map never goes grey while it zooms (Furkan, 10/10/2026: "zoomt niet soepel").
   */
  const [backLevel, setBackLevel] = useState<number | null>(null);
  /** The fingers and the mouse on the map, by pointer. */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  /** A drag (one finger or the mouse) or a pinch (two fingers), from where and which view it started. */
  const gesture = useRef<
    | { kind: "drag"; x: number; y: number; view: View }
    | { kind: "pinch"; distance: number; middle: { left: number; top: number }; view: View }
    | null
  >(null);
  const animation = useRef<number | null>(null);
  const settle = useRef<number | null>(null);
  /** The tiles the browser has. */
  const loaded = useRef(new Set<string>());

  useLayoutEffect(() => {
    const element = frame.current;
    if (!element) return;
    const measure = () => {
      const { width, height } = element.getBoundingClientRect();
      if (width > 0 && height > 0) setSize({ width: Math.round(width), height: Math.round(height) });
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(element);
    return () => observer?.disconnect();
  }, []);

  useEffect(
    () => () => {
      if (animation.current !== null) cancelAnimationFrame(animation.current);
      if (settle.current !== null) window.clearTimeout(settle.current);
    },
    [],
  );

  // the wheel and a pinch on a trackpad zoom the map, not the page around it (React listens passively)
  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    const keep = (event: WheelEvent) => {
      event.preventDefault();
    };
    element.addEventListener("wheel", keep, { passive: false });
    return () => {
      element.removeEventListener("wheel", keep);
    };
  }, []);

  const minLevel = map.tiles?.minLevel ?? MIN_LEVEL;
  const maxLevel = map.tiles?.maxLevel ?? MAX_LEVEL;
  /** What the map follows: the shop, every courier who told where he is and every address on the way. */
  const followed = useMemo(() => {
    const points: Point[] = [];
    if (map.shop) points.push(map.shop);
    for (const courier of map.couriers) {
      if (courier.position) points.push(courier.position);
      for (const stop of courier.stops) {
        const point = placed(stop);
        if (point) points.push(point);
      }
    }
    if (ordersShown) {
      for (const stop of map.waiting) {
        const point = placed(stop);
        if (point) points.push(point);
      }
    }
    if (points.length === 0 && map.centre) points.push(map.centre);
    return points;
  }, [map, ordersShown]);
  const fitted = fitView(followed, size, minLevel, maxLevel);
  const view = chosen ?? fitted;
  const clamp = (level: number) => Math.min(maxLevel, Math.max(minLevel, level));
  const tileLevel = tileLevelOf(view?.level ?? minLevel, minLevel, maxLevel);

  const stop = () => {
    if (animation.current !== null) cancelAnimationFrame(animation.current);
    animation.current = null;
    if (settle.current !== null) window.clearTimeout(settle.current);
    settle.current = null;
  };

  /** The tiles there were, kept under the new ones while those come. */
  const keepTiles = (from: View) => {
    const level = tileLevelOf(from.level, minLevel, maxLevel);
    setBackLevel((current) => current ?? level);
  };

  /**
   * Zooms to `level`, the point under `focus` staying where it is, in a quarter of a second: every frame
   * the tiles there are, scaled, and the new ones as they come.
   */
  const zoomTo = (from: View, level: number, focus: { left: number; top: number }) => {
    stop();
    const target = clamp(level);
    if (target === from.level) {
      setChosen(from);
      return;
    }
    keepTiles(from);
    if (typeof requestAnimationFrame === "undefined" || lessMotion()) {
      setChosen(zoomedAbout(from, size, focus, target));
      return;
    }
    // the time of the first frame is the start: the clock of the frames is the browser's own
    let start: number | null = null;
    const frameAt = (time: number) => {
      start ??= time;
      const progress = Math.min(1, Math.max(0, (time - start) / ZOOM_MS));
      const at = from.level + (target - from.level) * easeOut(progress);
      setChosen(zoomedAbout(from, size, focus, progress === 1 ? target : at));
      animation.current = progress === 1 ? null : requestAnimationFrame(frameAt);
    };
    animation.current = requestAnimationFrame(frameAt);
  };

  /** One level in or out (the buttons, a notch of the wheel, a double tap): from the level there is. */
  const step = (direction: 1 | -1, focus = { left: size.width / 2, top: size.height / 2 }) => {
    if (!view) return;
    const from = Math.round(view.level);
    zoomTo(view, from + direction, focus);
  };

  /** Between two levels (a pinch, a trackpad): to the nearest one, once the hand is still. */
  const settleLater = (current: View, focus: { left: number; top: number }) => {
    if (settle.current !== null) window.clearTimeout(settle.current);
    settle.current = window.setTimeout(() => {
      settle.current = null;
      zoomTo(current, Math.round(current.level), focus);
    }, SETTLE_AFTER_MS);
  };

  const local = (event: { clientX: number; clientY: number }) => {
    const box = frame.current?.getBoundingClientRect();
    return { left: event.clientX - (box?.left ?? 0), top: event.clientY - (box?.top ?? 0) };
  };

  const colours = new Map(map.couriers.map((courier, index) => [courier.id, courierColour(index)]));
  const on = (point: Point) => (view ? toScreen(view, size, point) : { left: 0, top: 0 });
  const seconds = (at: string) => Math.max(0, Math.round((now - new Date(at).getTime()) / 1000));
  /**
   * Where each courier is drawn: a new position glides there in a second, on the map itself, so the mark
   * moves with the map when it is dragged or zoomed (it used to glide across the screen after it).
   */
  const shown = useGlide(
    useMemo(
      () => new Map(map.couriers.flatMap((courier) => (courier.position ? [[courier.id, courier.position]] : []))),
      [map],
    ),
  );

  const front = view && map.tiles ? visibleTiles(view, size, tileLevel) : [];
  /** A tile of the level now came (or failed): the tiles before go once they all did. */
  const arrived = (src: string) => {
    loaded.current.add(src);
    if (front.every((tile) => loaded.current.has(tileUrl(map.tiles!.url, tile)))) setBackLevel(null);
  };
  // the tiles before, only while they are not much smaller or larger than those of now
  const back =
    view && map.tiles && backLevel !== null && backLevel !== tileLevel && Math.abs(backLevel - view.level) <= 2
      ? visibleTiles(view, size, backLevel)
      : [];

  return (
    <div className="panel livemap">
      <div
        className={`lm-map${map.tiles ? "" : " bare"}`}
        ref={frame}
        role="region"
        aria-label={text.region}
        onPointerDown={(event) => {
          if (!view || (event.target as HTMLElement).closest("button")) return;
          stop();
          pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
          try {
            // the drag goes on when the hand leaves the map (not in every test browser)
            event.currentTarget.setPointerCapture(event.pointerId);
          } catch {
            // without it the drag stops at the edge of the map
          }
          const fingers = [...pointers.current.values()];
          if (fingers.length >= 2) {
            const [a, b] = fingers as [{ x: number; y: number }, { x: number; y: number }];
            keepTiles(view);
            gesture.current = {
              kind: "pinch",
              distance: Math.max(1, distance(a, b)),
              middle: local({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 }),
              view,
            };
          } else {
            gesture.current = { kind: "drag", x: event.clientX, y: event.clientY, view };
          }
        }}
        onPointerMove={(event) => {
          if (!pointers.current.has(event.pointerId)) return;
          pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
          const now = gesture.current;
          if (!now) return;
          if (now.kind === "drag") {
            setChosen(panned(now.view, event.clientX - now.x, event.clientY - now.y));
            return;
          }
          const [a, b] = [...pointers.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
          const level = clamp(now.view.level + Math.log2(Math.max(1, distance(a, b)) / now.distance));
          const middle = local({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 });
          // the map grows round the first middle of the fingers, and follows them when they move
          const zoomed = zoomedAbout(now.view, size, now.middle, level);
          setChosen(panned(zoomed, middle.left - now.middle.left, middle.top - now.middle.top));
        }}
        onPointerUp={(event) => {
          pointers.current.delete(event.pointerId);
          const was = gesture.current;
          gesture.current = null;
          if (was?.kind === "pinch" && chosen) {
            // the other finger may stay: it drags on from here
            const left = [...pointers.current.values()][0];
            if (left) gesture.current = { kind: "drag", x: left.x, y: left.y, view: chosen };
            zoomTo(chosen, Math.round(chosen.level), was.middle);
          }
        }}
        onPointerCancel={(event) => {
          pointers.current.delete(event.pointerId);
          gesture.current = null;
        }}
        onDoubleClick={(event) => {
          if ((event.target as HTMLElement).closest("button")) return;
          step(event.shiftKey ? -1 : 1, local(event));
        }}
        onWheel={(event) => {
          if (!view || event.deltaY === 0) return;
          const focus = local(event);
          // a notch of a mouse: one level, gliding; a trackpad or a pinch on it: as far as the hand goes
          if (event.deltaMode !== 0 || (!event.ctrlKey && Math.abs(event.deltaY) >= NOTCH)) {
            step(event.deltaY < 0 ? 1 : -1, focus);
            return;
          }
          stop();
          keepTiles(view);
          const next = zoomedAbout(view, size, focus, clamp(view.level - event.deltaY / (event.ctrlKey ? 60 : 120)));
          setChosen(next);
          settleLater(next, focus);
        }}
      >
        {view && map.tiles && (
          <div className="lm-tiles" aria-hidden="true">
            {back.map((tile) => {
              const src = tileUrl(map.tiles!.url, tile);
              return (
                <img
                  key={`back ${src}`}
                  src={src}
                  alt=""
                  draggable={false}
                  style={{ left: tile.left, top: tile.top, width: tile.size, height: tile.size }}
                  onError={(event) => {
                    event.currentTarget.style.visibility = "hidden";
                  }}
                />
              );
            })}
            {front.map((tile) => {
              const src = tileUrl(map.tiles!.url, tile);
              return (
                <img
                  // by its address: a square that failed is tried again with the next key
                  key={src}
                  src={src}
                  alt=""
                  draggable={false}
                  style={{ left: tile.left, top: tile.top, width: tile.size, height: tile.size }}
                  onLoad={() => {
                    arrived(src);
                  }}
                  onError={(event) => {
                    // no tile there (outside Flanders) or the service is down: that square stays empty
                    event.currentTarget.style.visibility = "hidden";
                    arrived(src);
                  }}
                />
              );
            })}
          </div>
        )}

        {view && (
          <svg className="lm-lines" width={size.width} height={size.height} aria-hidden="true">
            {map.couriers.map((courier) => {
              const from = shown.get(courier.id);
              const way = [
                ...(from ? [from] : []),
                ...courier.stops.filter((stop) => stop.departed).flatMap((stop) => placed(stop) ?? []),
                // on his way back: the line goes to the shop
                ...(courier.returning && map.shop ? [map.shop] : []),
              ].map(on);
              if (way.length < 2) return null;
              return (
                <polyline
                  key={courier.id}
                  points={way.map((point) => `${String(point.left)},${String(point.top)}`).join(" ")}
                  stroke={colours.get(courier.id)}
                />
              );
            })}
          </svg>
        )}

        {view &&
          ordersShown &&
          map.waiting.map((stop) => {
            const point = placed(stop);
            if (!point) return null;
            const { left, top } = on(point);
            return (
              <span
                key={stop.orderId}
                className="lm-stop lm-open-order"
                style={{ left, top }}
                title={[stop.address, stop.zone].filter(Boolean).join(", ")}
              >
                <b>{stop.code}</b>
                {stop.address}
              </span>
            );
          })}

        {view &&
          map.couriers.flatMap((courier) =>
            courier.stops.map((stop, index) => {
              const point = placed(stop);
              if (!point) return null;
              const { left, top } = on(point);
              return (
                <span
                  key={stop.orderId}
                  className={`lm-stop${stop.departed ? "" : " later"}`}
                  style={{ left, top, borderColor: colours.get(courier.id), color: colours.get(courier.id) }}
                  title={`${courier.name} · ${stop.address}`}
                >
                  <b style={{ background: colours.get(courier.id) }}>{index + 1}</b>
                  {stop.code}
                </span>
              );
            }),
          )}

        {view && map.shop && (
          <span className="lm-shop" style={on(map.shop)} title={map.shop.name}>
            <span aria-hidden="true">🏠</span>
            <small>{map.shop.name}</small>
          </span>
        )}

        {view &&
          map.couriers.map((courier) => {
            const point = shown.get(courier.id);
            if (!courier.position || !point) return null;
            const { left, top } = on(point);
            return (
              <span
                key={courier.id}
                className={`lm-courier${courier.position.fresh ? "" : " stale"}`}
                style={{ left, top, background: colours.get(courier.id), color: colours.get(courier.id) }}
                aria-label={courier.name}
              >
                <span className="lm-initial">{courier.name.slice(0, 1).toUpperCase()}</span>
                <small>{courier.name}</small>
              </span>
            );
          })}

        <div className="lm-zoom">
          <button type="button" className="btn sm" aria-label={text.zoomIn} onClick={() => step(1)}>
            +
          </button>
          <button type="button" className="btn sm" aria-label={text.zoomOut} onClick={() => step(-1)}>
            −
          </button>
          {chosen && (
            <button
              type="button"
              className="btn sm"
              onClick={() => {
                stop();
                setChosen(null);
              }}
            >
              {text.showAll}
            </button>
          )}
        </div>
        {map.tiles ? (
          <small className="lm-credit">{map.tiles.attribution}</small>
        ) : (
          <small className="lm-credit">{text.noMap}</small>
        )}
      </div>

      <aside className="lm-side">
        <h2>{text.title}</h2>
        <button
          type="button"
          className="btn lm-orders-btn"
          aria-pressed={ordersShown}
          onClick={() => {
            setOrdersShown(!ordersShown);
            // the map takes them in at once (or leaves them out again)
            stop();
            setChosen(null);
          }}
        >
          {ordersShown ? text.hideOrders : text.showOrders(map.waiting.length)}
        </button>
        {map.couriers.length === 0 && (
          <div className="lm-card">
            <b>{text.noCouriers}</b>
            <span className="note">{text.noCouriersNote}</span>
          </div>
        )}
        {/* the one who is back first on top: his next ride can be made ready (the colours stay by name) */}
        {map.couriers.toSorted(byReturn).map((courier) => (
          <CourierCard
            key={courier.id}
            courier={courier}
            colour={colours.get(courier.id)!}
            now={now}
            seconds={seconds}
            text={text}
          />
        ))}
        {map.waiting.length > 0 && (
          <div className="lm-card">
            <b>{text.waiting(map.waiting.length)}</b>
            <ol className="lm-stops">
              {map.waiting.map((stop) => (
                <li key={stop.orderId}>
                  <span>{text.stop(stop.code, [stop.address, stop.zone].filter(Boolean).join(", "))}</span>
                  {placed(stop) === null && <small className="note">{text.noPlace}</small>}
                </li>
              ))}
            </ol>
          </div>
        )}
        <p className="note">{text.privacy}</p>
      </aside>
    </div>
  );
}

function CourierCard({
  courier,
  colour,
  now,
  seconds,
  text,
}: {
  courier: LiveCourier;
  colour: string;
  now: number;
  seconds: (at: string) => number;
  text: typeof t.liveMap;
}) {
  const left = courier.stops.some((stop) => stop.departed);
  const status = courier.returning
    ? text.returning
    : !left
      ? text.notLeft
      : courier.position === null
        ? text.noGps
        : courier.position.fresh
          ? text.live(text.ago(seconds(courier.position.at)))
          : text.lastSeen(text.ago(seconds(courier.position.at)));
  const backIn = courier.backAt === null ? null : Math.round((new Date(courier.backAt).getTime() - now) / 60_000);
  return (
    <div className="lm-card" style={{ borderLeftColor: colour }}>
      <div className="lm-head">
        <b>
          <span className="lm-dot" style={{ background: colour }} aria-hidden="true" />
          {courier.name}
        </b>
        <span className={`lm-status${courier.position?.fresh ? " on" : ""}`}>{status}</span>
      </div>
      {courier.backAt !== null && (
        <div className="lm-back">
          <b>{text.back(formatTime(courier.backAt))}</b>
          <span>{text.backIn(backIn ?? 0)}</span>
        </div>
      )}
      <ol className="lm-stops">
        {courier.stops.map((stop) => (
          <li key={stop.orderId} className={stop.departed ? "" : "later"}>
            <span>{text.stop(stop.code, [stop.address, stop.zone].filter(Boolean).join(", "))}</span>
            {stop.eta !== null && <small>{text.at(formatTime(stop.eta))}</small>}
            {placed(stop) === null && <small className="note">{text.noPlace}</small>}
          </li>
        ))}
      </ol>
    </div>
  );
}
